import { useEffect, useState } from "react";
import { getSupabase, supabaseConfigured } from "../lib/supabase";
import { useSession } from "../lib/useSession";
import { useLang, useTheme } from "../lib/prefs";
import { PrefsToggles } from "./PrefsToggles";
import { GoogleIcon } from "./GoogleIcon";

// /connect — the MCP OAuth consent screen. The Worker's /authorize endpoint
// redirects the browser here (preserving the OAuth query params) so we can run
// citada's normal Supabase Google sign-in and then bind the issued MCP grant to
// that Supabase user via POST /oauth/complete. The MCP user IS the Supabase user.
const RETURN_KEY = "citada_connect_return";

const T = {
  es: {
    title: "Conectar Claude",
    lead: "Un cliente MCP (como Claude) quiere leer los sitios y recomendaciones de tu cuenta citada.",
    scope: "Acceso de solo lectura: ver tus sitios, tus recomendaciones semanales y descargar sus archivos. No puede cambiar nada.",
    signinLead: "Inicia sesión con Google para autorizar la conexión.",
    signin: "Continuar con Google",
    connecting: "Conectando…",
    asYou: "Conectado como",
    authorize: "Autorizar",
    working: "Autorizando…",
    cancel: "Cancelar",
    noReq: "No hay una solicitud de conexión activa. Abre citada desde tu cliente MCP para conectar.",
    goApp: "Ir a citada",
    err: "No se pudo completar la conexión. Intenta de nuevo.",
    notConfigured: "Autenticación no configurada.",
  },
  en: {
    title: "Connect Claude",
    lead: "An MCP client (such as Claude) wants to read your citada account's sites and recommendations.",
    scope: "Read-only access: view your sites, your weekly recommendations, and download their files. It cannot change anything.",
    signinLead: "Sign in with Google to authorize the connection.",
    signin: "Continue with Google",
    connecting: "Connecting…",
    asYou: "Connected as",
    authorize: "Authorize",
    working: "Authorizing…",
    cancel: "Cancel",
    noReq: "No active connection request. Open citada from your MCP client to connect.",
    goApp: "Go to citada",
    err: "Could not complete the connection. Please try again.",
    notConfigured: "Auth not configured.",
  },
};

export default function Connect() {
  const [lang, toggleLang] = useLang();
  const [, toggleTheme] = useTheme();
  const { loading, session } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = T[lang];

  // The OAuth params handed to us by the Worker's /authorize redirect.
  const search = window.location.search;
  const hasRequest = new URLSearchParams(search).has("client_id");

  async function signIn() {
    setError(null);
    if (!supabaseConfigured) {
      setError(t.notConfigured);
      return;
    }
    setBusy(true);
    // Remember where to come back to after the Google round-trip.
    sessionStorage.setItem(RETURN_KEY, `/connect${search}`);
    const { error } = await getSupabase().auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      setBusy(false);
      setError(t.err);
    }
  }

  async function authorize() {
    if (!session) return;
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`/oauth/complete${search}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        redirectTo?: string;
      };
      if (!res.ok || !data.ok || !data.redirectTo) {
        throw new Error("complete_failed");
      }
      // Hand control back to the MCP client with the authorization code.
      window.location.href = data.redirectTo;
    } catch {
      setBusy(false);
      setError(t.err);
    }
  }

  useEffect(() => {
    // Clear any stale return marker once we've landed back here signed-in.
    if (session) sessionStorage.removeItem(RETURN_KEY);
  }, [session]);

  return (
    <div className="auth">
      <div className="auth-topbar">
        <PrefsToggles lang={lang} toggleLang={toggleLang} toggleTheme={toggleTheme} />
      </div>
      <div className="auth-card">
        <div className="auth-logo">citada</div>
        <h1>{t.title}</h1>

        {!hasRequest ? (
          <>
            <p className="lead">{t.noReq}</p>
            <a className="g-btn" href="/">
              {t.goApp}
            </a>
          </>
        ) : loading ? (
          <div className="spinner" />
        ) : (
          <>
            <p className="lead">{t.lead}</p>
            <p className="sub-line" style={{ margin: "0 0 14px" }}>
              {t.scope}
            </p>
            {error && <p className="auth-err">{error}</p>}

            {!session ? (
              <>
                <p className="sub-line" style={{ margin: "0 0 12px" }}>
                  {t.signinLead}
                </p>
                <button className="g-btn" onClick={signIn} disabled={busy}>
                  <GoogleIcon />
                  {busy ? t.connecting : t.signin}
                </button>
              </>
            ) : (
              <>
                <p className="sub-line" style={{ margin: "0 0 14px" }}>
                  {t.asYou} <b>{session.user.email}</b>
                </p>
                <button
                  className="btn btn-primary btn-block"
                  onClick={authorize}
                  disabled={busy}
                >
                  {busy ? t.working : t.authorize}
                </button>
                <button
                  className="btn btn-ghost btn-block"
                  style={{ marginTop: 10 }}
                  onClick={() => (window.location.href = "/")}
                  disabled={busy}
                >
                  {t.cancel}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
